import React, { useState, KeyboardEvent, MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { IconButton, Typography, Toolbar, Box, AppBar, Drawer, List, ListItem, Divider, Button, Stack, Menu, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import DevicesIcon from '@mui/icons-material/Devices';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChargingStationIcon from '@mui/icons-material/EvStation';
import CableIcon from '@mui/icons-material/Cable';
import ComputerIcon from '@mui/icons-material/Computer';
import SettingsIcon from '@mui/icons-material/Settings';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useThemeMode } from '../../App';

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [plugsMenuAnchor, setPlugsMenuAnchor] = useState<null | HTMLElement>(null);
  const plugsMenuOpen = Boolean(plugsMenuAnchor);
  const openPlugsMenu = (event: MouseEvent<HTMLElement>) => setPlugsMenuAnchor(event.currentTarget);
  const closePlugsMenu = () => setPlugsMenuAnchor(null);
  const [dashboardMenuAnchor, setDashboardMenuAnchor] = useState<null | HTMLElement>(null);
  const dashboardMenuOpen = Boolean(dashboardMenuAnchor);
  const openDashboardMenu = (event: MouseEvent<HTMLElement>) => setDashboardMenuAnchor(event.currentTarget);
  const closeDashboardMenu = () => setDashboardMenuAnchor(null);
  const { colorScheme, toggleColorScheme } = useThemeMode();
  const isFpmHost = window.location.hostname === 'fpm.odp.golf';
  const nextHost = isFpmHost ? 'odp.golf' : 'fpm.odp.golf';

  const toggleDomain = () => {
    const { protocol } = window.location;
    window.location.href = `${protocol}//${nextHost}/?v=${Date.now()}`;
  };

  const toggleDrawer = (open: boolean) => (event: KeyboardEvent | MouseEvent) => {
    if (((event as KeyboardEvent).key === 'Tab') || ((event as KeyboardEvent).key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const drawerList = () => (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        <ListItem>
          <Button
            component={Link}
            to="/setup"
            startIcon={<SettingsIcon />}
            sx={{ justifyContent: 'flex-start', width: '100%' }}
          >
            Setup
          </Button>
        </ListItem>
        <ListItem sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, color: 'text.secondary' }}>
            <DashboardIcon fontSize="small" />
            <Typography variant="overline">Dashboard</Typography>
          </Box>
        </ListItem>
        <ListItem sx={{ py: 0 }}>
          <Button
            component={Link}
            to="/"
            sx={{ justifyContent: 'flex-start', width: '100%', pl: 4 }}
          >
            Charging
          </Button>
        </ListItem>
        <ListItem sx={{ py: 0 }}>
          <Button
            component={Link}
            to="/dashboard/energy"
            sx={{ justifyContent: 'flex-start', width: '100%', pl: 4 }}
          >
            Energy &amp; Solar
          </Button>
        </ListItem>
        <ListItem>
          <Button
            component={Link}
            to="/power"
            startIcon={<DevicesIcon />}
            sx={{ justifyContent: 'flex-start', width: '100%' }}
          >
            Power
          </Button>
        </ListItem>
        <ListItem>
          <Button
            component={Link}
            to="/chargers"
            startIcon={<ChargingStationIcon />}
            sx={{ justifyContent: 'flex-start', width: '100%' }}
          >
            Chargers
          </Button>
        </ListItem>
        <ListItem sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, color: 'text.secondary' }}>
            <CableIcon fontSize="small" />
            <Typography variant="overline">Plugs</Typography>
          </Box>
        </ListItem>
        <ListItem sx={{ py: 0 }}>
          <Button
            component={Link}
            to="/plugs/commission"
            sx={{ justifyContent: 'flex-start', width: '100%', pl: 4 }}
          >
            Commission
          </Button>
        </ListItem>
        <ListItem sx={{ py: 0 }}>
          <Button
            component={Link}
            to="/plugs/edit"
            sx={{ justifyContent: 'flex-start', width: '100%', pl: 4 }}
          >
            Edit
          </Button>
        </ListItem>
        <ListItem>
          <Button
            component={Link}
            to="/fleet"
            startIcon={<DirectionsCarIcon />}
            sx={{ justifyContent: 'flex-start', width: '100%' }}
          >
            Fleet
          </Button>
        </ListItem>
        <ListItem>
          <Button
            component={Link}
            to="/events"
            startIcon={<NotificationsActiveIcon />}
            sx={{ justifyContent: 'flex-start', width: '100%' }}
          >
            Events
          </Button>
        </ListItem>
        <ListItem>
          <Button
            component={Link}
            to="/nuc"
            startIcon={<ComputerIcon />}
            sx={{ justifyContent: 'flex-start', width: '100%' }}
          >
            NUC
          </Button>
        </ListItem>
        <ListItem>
          <Button
            onClick={toggleDomain}
            sx={{ justifyContent: 'flex-start', width: '100%', textTransform: 'none' }}
          >
            {isFpmHost ? 'Go to odp.golf' : 'Go to fpm'}
          </Button>
        </ListItem>
        <ListItem>
            <Button
            component="a"
            href="/phpmyadmin"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<ChargingStationIcon />}
            sx={{ justifyContent: 'flex-start', width: '100%' }}
          >
            Database
          </Button>
        </ListItem>
      </List>
      <Divider />
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ backgroundColor: '#1e212a', paddingTop: '8px' }}>
        <Toolbar>
          {/* Logo */}
          <img
            src={`${process.env.PUBLIC_URL}/logos/ODP_Icon.svg`}
            alt="Company Logo"
            style={{ width: '42px', height: '42px', marginRight: '16px' }}
          />

          {/* IconButton for Drawer Toggle */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ display: { xs: 'block', md: 'none' }, marginRight: 2 }}
            onClick={toggleDrawer(true)}
          >
            <MenuIcon />
          </IconButton>

          {/* Links for larger screens */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Typography variant="subtitle1" component="div" sx={{ color: 'white', marginRight: '20px' }}>
              <Link to="/setup" style={{ color: '#FFFF00', textDecoration: 'none' }}>Setup</Link>
            </Typography>
            <Box sx={{ marginRight: '20px' }}>
              <Button
                onClick={openDashboardMenu}
                endIcon={<ArrowDropDownIcon />}
                sx={{
                  color: '#FFFF00',
                  textTransform: 'none',
                  fontSize: '1rem',
                  p: 0,
                  minWidth: 0,
                  '&:hover': { backgroundColor: 'transparent' },
                }}
              >
                Dashboard
              </Button>
              <Menu anchorEl={dashboardMenuAnchor} open={dashboardMenuOpen} onClose={closeDashboardMenu}>
                <MenuItem component={Link} to="/" onClick={closeDashboardMenu}>
                  Charging
                </MenuItem>
                <MenuItem component={Link} to="/dashboard/energy" onClick={closeDashboardMenu}>
                  Energy &amp; Solar
                </MenuItem>
              </Menu>
            </Box>
            <Typography variant="subtitle1" component="div" sx={{ color: 'white', marginRight: '20px' }}>
              <Link to="/power" style={{ color: '#FFFF00', textDecoration: 'none' }}>Power</Link>
            </Typography>
            <Typography variant="subtitle1" component="div" sx={{ color: 'white', marginRight: '20px' }}>
              <Link to="/chargers" style={{ color: '#FFFF00', textDecoration: 'none' }}>Chargers</Link>
            </Typography>
            <Box sx={{ marginRight: '20px' }}>
              <Button
                onClick={openPlugsMenu}
                endIcon={<ArrowDropDownIcon />}
                sx={{
                  color: '#FFFF00',
                  textTransform: 'none',
                  fontSize: '1rem',
                  p: 0,
                  minWidth: 0,
                  '&:hover': { backgroundColor: 'transparent' },
                }}
              >
                Plugs
              </Button>
              <Menu anchorEl={plugsMenuAnchor} open={plugsMenuOpen} onClose={closePlugsMenu}>
                <MenuItem component={Link} to="/plugs/commission" onClick={closePlugsMenu}>
                  Commission
                </MenuItem>
                <MenuItem component={Link} to="/plugs/edit" onClick={closePlugsMenu}>
                  Edit
                </MenuItem>
              </Menu>
            </Box>
            <Typography variant="subtitle1" component="div" sx={{ color: 'white', marginRight: '20px' }}>
              <Link to="/fleet" style={{ color: '#FFFF00', textDecoration: 'none' }}>Fleet</Link>
            </Typography>
            <Typography variant="subtitle1" component="div" sx={{ color: 'white', marginRight: '20px' }}>
              <Link to="/events" style={{ color: '#FFFF00', textDecoration: 'none' }}>Events</Link>
            </Typography>
            <Typography variant="subtitle1" component="div" sx={{ color: 'white', marginRight: '20px' }}>
              <Link to="/nuc" style={{ color: '#FFFF00', textDecoration: 'none' }}>NUC</Link>
            </Typography>
            <Typography variant="subtitle1" component="div" sx={{ color: 'white', marginRight: '20px' }}>
              <a href="/phpmyadmin" target="_blank" rel="noopener noreferrer" style={{ color: '#FFFF00', textDecoration: 'none' }}>Database</a>
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, marginLeft: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={toggleDomain}
              sx={{
                color: '#FFFF00',
                borderColor: '#FFFF00',
                whiteSpace: 'nowrap',
                marginRight: 1.5,
                textTransform: 'none',
                '&:hover': { borderColor: '#FFFF00' },
              }}
            >
              {isFpmHost ? 'Go to odp.golf' : 'Go to fpm'}
            </Button>

            {/* Theme Toggle Button */}
            <IconButton
              size="large"
              aria-label="toggle theme"
              onClick={toggleColorScheme}
              sx={{ color: '#FFFF00' }}
            >
              {colorScheme === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Stack>

          {/* Drawer Component */}
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
          >
            {drawerList()}
          </Drawer>
        </Toolbar>
      </AppBar>
    </Box>
  );
}